/**
 * Control for the `userAndGroup` field type: a Select (users or business groups)
 * or a business-group → user Cascader. When the field type sets `enableWeights`,
 * its dropdown adds one full-width allocation row per selection, keeping the
 * selector itself compact in forms and tables alike.
 * Rendered by `ControlUI`, which forwards the field type's `props`; all option
 * building and value translation lives in `useUserAndGroupUI`.
 * Invariants: read-only and PDF paths render the same weights as tags; editable
 * weighted fields use the same dropdown allocation UI in every layout context;
 * when `enforceWeightTotal` is on, an out-of-target sum forces the control's
 * status to `error` regardless of the externally passed status, so the closed
 * selector still signals the invalid allocation.
 */
import { FC, ReactElement } from 'react';
import { Select, Cascader, Tag, InputNumber, Typography, Divider, theme } from 'antd';
import { UserAndGroupValue, UserAndGroupSelection, UserAndGroupFieldTypeProps } from '#types';
import { useUserAndGroupUI } from './useUserAndGroupUI';
import FlexBox from '../../../atoms/FlexBox';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';

const { Text } = Typography;

interface UserAndGroupUIProps extends UserAndGroupFieldTypeProps {
    value: string | UserAndGroupValue | null | undefined;
    onChange: (value: UserAndGroupValue) => void;
    disabled?: boolean;
    readOnly?: boolean;
    status?: '' | 'error' | 'warning' | undefined;
    [key: string]: any;
}

const UserAndGroupUI: FC<UserAndGroupUIProps> = ({
    value,
    onChange,
    disabled = false,
    readOnly = false,
    status,
    selectionMode = 'usersOnly',
    multiSelect = false,
    restrictToBusinessGroupIds,
    enableWeights = false,
    maxSelections,
    enforceWeightTotal = false,
    targetWeightTotal,
    ...props
}) => {
    const { style, variant, ...restProps } = props;
    const { isPdfExportMode } = usePdfExportContext();
    const { token } = theme.useToken();

    const {
        selections,
        selectOptions,
        cascaderOptions,
        isCascaderMode,
        selectValue,
        cascaderValue,
        selectionCap,
        weightTotal,
        handleSelectChange,
        handleCascaderChange,
        handleWeightChange,
        handleClear,
        userNameMap,
        groupNameMap,
    } = useUserAndGroupUI({
        value,
        onChange,
        selectionMode,
        multiSelect,
        restrictToBusinessGroupIds,
        maxSelections,
        enableWeights,
        enforceWeightTotal,
        targetWeightTotal,
    });

    const effectiveStatus = weightTotal && !weightTotal.isValid ? 'error' : status;

    const labelFor = (selection: UserAndGroupSelection): string =>
        selection.type === 'group'
            ? groupNameMap.get(selection.id) ?? selection.name ?? selection.id
            : userNameMap.get(selection.id) ?? selection.name ?? selection.id;

    if (isPdfExportMode || (readOnly && !disabled)) {
        const formatted = typeof value === 'string' ? value : value?.formatted;
        return (
            <FlexBox style={{ flexWrap: 'wrap', gap: 5, minHeight: 32, alignItems: 'center', ...style }}>
                {selections.length === 0 && <Tag>{formatted || 'None'}</Tag>}
                {selections.map((selection) => (
                    <Tag
                        key={`${selection.type}-${selection.id}`}
                        color={selection.type === 'group' ? 'blue' : undefined}
                    >
                        {selection.weightPercentage === undefined
                            ? labelFor(selection)
                            : `${labelFor(selection)} (${selection.weightPercentage}%)`}
                    </Tag>
                ))}
            </FlexBox>
        );
    }

    const baseProps = {
        ...restProps,
        style: { width: '100%', ...style },
        variant: variant as any,
        disabled,
        status: effectiveStatus,
        allowClear: true,
        onClear: handleClear,
    };

    const weightRows = enableWeights && selections.length > 0 && (
        <FlexBox
            column
            noGrow
            gap={token.marginXXS}
            padding={`0 ${token.paddingXS}px ${token.paddingXS}px`}
            role="group"
            aria-label="user-and-group-weight-list"
            onMouseDown={(event) => event.stopPropagation()}
            style={{ width: '100%', boxSizing: 'border-box' }}
        >
            <FlexBox
                noGrow
                gap={0}
                padding={`0 ${token.paddingSM}px`}
                style={{ width: '100%', boxSizing: 'border-box' }}
            >
                <Text type="secondary" style={{ flex: 1, fontSize: token.fontSizeSM }}>
                    Selected
                </Text>
                <Text type="secondary" style={{ width: 76, textAlign: 'right', fontSize: token.fontSizeSM }}>
                    Percent
                </Text>
            </FlexBox>
            {selections.map((selection) => (
                <FlexBox
                    key={`weight-${selection.type}-${selection.id}`}
                    noGrow
                    gap={0}
                    padding={`0 ${token.paddingSM}px`}
                    aria-label={`user-and-group-weight-row-${selection.id}`}
                    style={{
                        width: '100%',
                        minHeight: token.controlHeight,
                        borderRadius: token.borderRadiusSM,
                        background: token.colorFillQuaternary,
                        boxSizing: 'border-box',
                    }}
                >
                    <Text
                        ellipsis={{ tooltip: labelFor(selection) }}
                        style={{ flex: 1, minWidth: 0, paddingRight: token.paddingSM, fontWeight: 500 }}
                    >
                        {labelFor(selection)}
                    </Text>
                    <InputNumber<number>
                        value={selection.weightPercentage}
                        onChange={(weight) => handleWeightChange(selection.id, weight)}
                        min={0}
                        max={100}
                        suffix="%"
                        placeholder="0"
                        variant="borderless"
                        controls={false}
                        disabled={disabled}
                        status={status}
                        aria-label={`user-and-group-weight-${selection.id}`}
                        style={{ width: 76 }}
                        styles={{
                            input: {
                                paddingInline: 0,
                                textAlign: 'right',
                                fontWeight: 600,
                                fontVariantNumeric: 'tabular-nums',
                            },
                        }}
                    />
                </FlexBox>
            ))}
            {weightTotal && (
                <FlexBox
                    noGrow
                    gap={0}
                    padding={`0 ${token.paddingSM}px`}
                    aria-label="user-and-group-weight-total"
                    style={{
                        width: '100%',
                        minHeight: token.controlHeight,
                        boxSizing: 'border-box',
                        alignItems: 'center',
                    }}
                >
                    <Text
                        strong
                        style={{
                            flex: 1,
                            minWidth: 0,
                            color: weightTotal.isValid ? token.colorSuccess : token.colorError,
                        }}
                    >
                        Total
                    </Text>
                    <Text
                        strong
                        style={{
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                            color: weightTotal.isValid ? token.colorSuccess : token.colorError,
                        }}
                    >
                        {`${weightTotal.sum}% / ${weightTotal.target}%`}
                    </Text>
                </FlexBox>
            )}
        </FlexBox>
    );

    const renderPopup = (menu: ReactElement): ReactElement => (
        <div>
            {menu}
            {weightRows && (
                <>
                    <Divider style={{ margin: `${token.marginXS}px 0` }} />
                    {weightRows}
                </>
            )}
        </div>
    );

    if (isCascaderMode) {
        const cascaderBaseProps = {
            ...baseProps,
            options: cascaderOptions,
            showSearch: true,
            changeOnSelect: selectionMode === 'cascadeUsersAndGroups',
            onChange: handleCascaderChange as any,
            popupRender: enableWeights ? renderPopup : undefined,
            'aria-label': 'user-and-group-cascader',
        };
        return multiSelect ? (
            <Cascader {...cascaderBaseProps} multiple value={cascaderValue as string[][]} />
        ) : (
            <Cascader {...cascaderBaseProps} value={cascaderValue as string[]} />
        );
    }

    return (
        <Select
            {...baseProps}
            showSearch={{ optionFilterProp: 'label' }}
            maxTagCount={3}
            maxCount={multiSelect ? selectionCap : undefined}
            options={selectOptions}
            value={selectValue}
            mode={multiSelect ? 'multiple' : undefined}
            onChange={handleSelectChange}
            popupRender={enableWeights ? renderPopup : undefined}
            aria-label="user-and-group-select"
        />
    );
};

export default UserAndGroupUI;
